import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { BorderTopProps, FlexProps, HeightProps } from 'styled-system';
import { borderTop, flex, height, themeGet } from 'styled-system';

import Alert from 'common/components/Alert';
import { Button } from 'common/components/Button';
import { Flex } from 'common/components/Flex';
import SelectProductCard from 'common/components/SelectProductCard';
import Spinner from 'common/components/Spinner';
import { HeadlineH7 } from 'common/components/Typography';
import { MessageType } from 'common/enums/Alert';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { OverflowYProps } from 'common/theme/system-utilities';
import { overflowY } from 'common/theme/system-utilities';
import type { DispatchState } from 'common/types/React';
import ProductDetail from '../ProductDrawer/ProductDetail';
import ProductImage from '../ProductDrawer/ProductImage';
import SelectAll from '../ProductDrawer/SelectAll';
import type { ProductDetails } from '../types';

interface ProductListProps {
  orderId: string;
  products: ProductDetails[];
  required?: boolean;
  isLoading: boolean;
  selectedProducts: string[];
  onClose: () => void;
  setProducts: (param: string[]) => void;
  setSelectedProducts: DispatchState<string[]>;
}

const Container = styled(Flex)<HeightProps>`
  ${height};
`;
const containerStyles: ComponentProps<typeof Container> = {
  height: '100%',
  flexDirection: 'column',
};

const ProductsContainer = styled(Flex)<FlexProps & OverflowYProps>`
  ${overflowY};
`;
const productsContainerStyles: ComponentProps<typeof ProductsContainer> = {
  flexDirection: 'column',
  px: ['sp_16', null, null, 'sp_20'],
  mb: ['sp_16', null, 'sp_20', 'sp_24'],
  flex: 1,
  overflowY: 'auto',
};

const Footer = styled(Flex)<BorderTopProps>`
  ${borderTop};

  box-shadow: 0 1px 8px 0 ${themeGet('colors.overlay')};
`;
const footerStyles = {
  px: 'sp_16',
  py: 'sp_8',
  borderTop: 3,
};

const SelectButton = styled(Button)<FlexProps>`
  ${flex};
`;

export const ProductList = ({
  orderId,
  products,
  isLoading,
  selectedProducts,
  onClose,
  setProducts,
  setSelectedProducts,
}: ProductListProps) => {
  const { t } = useLocalization();

  const onSelectProduct = (lineNo: string) => {
    setSelectedProducts(
      selectedProducts.includes(lineNo)
        ? selectedProducts.filter(selected => selected !== lineNo)
        : [lineNo, ...selectedProducts]
    );
  };

  const onClickSelectButton = () => {
    setProducts(selectedProducts);
    onClose();
  };

  return (
    <Container {...containerStyles}>
      <ProductsContainer {...productsContainerStyles}>
        {products.length === 0 && !isLoading ? (
          <Alert type={MessageType.Warning} mt={['sp_16', null, 'sp_20', 'sp_24']}>
            {t('h24_products_not_found_for_selected_order')}
          </Alert>
        ) : (
          <>
            <HeadlineH7 my={['sp_16', null, 'sp_20', 'sp_24']}>
              {t('h24_product_selection_title')}
            </HeadlineH7>
            <SelectAll
              products={products}
              selectedProducts={selectedProducts}
              setSelectedProducts={setSelectedProducts}
            />
          </>
        )}
        {isLoading && <Spinner size={32} margin="sp_12" strokeColor="sys.neutral.border.hover" />}
        {products.map(item => {
          const { lineNo, article, price } = item;
          const isSelected = selectedProducts.includes(lineNo);
          const id = orderId + article?.sku + lineNo;

          return (
            article !== null && (
              <SelectProductCard
                key={id}
                value={lineNo}
                isSelected={isSelected}
                onSelectProduct={onSelectProduct}
                ariaLabelledBy={id}
              >
                <ProductImage imageAlt={article.name} imagePath={article.images?.[0]?.path} />
                <ProductDetail
                  nameId={id}
                  name={article.name}
                  variantName={article.variantName}
                  price={price}
                />
              </SelectProductCard>
            )
          );
        })}
      </ProductsContainer>

      <Footer {...footerStyles}>
        <SelectButton onClick={onClickSelectButton} flex="1">
          {t('h24_save_selection')}
        </SelectButton>
      </Footer>
    </Container>
  );
};
